import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { Box, Typography, LinearProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Skeleton from '@material-ui/lab/Skeleton'

import { B2CCard, DottedDivider } from '@masfront/common-b2c/src/modules/ui'
import { CustomTheme } from '@masfront/common-b2c/src/modules/ui/theme/utils'

import ErrorAndRetry from './ErrorAndRetry'

import { selectAgent } from '~/auth/store/auth.selectors'
import {
  isUnlimitedVoiceTariff,
  isUnlimitedDataTariff,
  totalVoiceInTariff,
  totalDataInTariff,
} from '~/bonus/helpers/bonus.helpers'
import useBonus from '~/bonus/hooks/useBonus'
import { useBrand } from '~/brand'
import {
  dataConsumed,
  voiceConsumed,
  remainingSubscriptionData,
  ratioVoiceConsumed,
  ratioDataConsumed,
  totalVoiceConsumed,
} from '~/consumptions/helpers/consumption.helpers'
import useConsumption from '~/consumptions/useConsumption'
import { hasConsumptionData, getTariffId, getProvinceId } from '~/customer/helpers'
import { Subscription } from '~/customer/model/customer.model'
import useCustomer from '~/customer/useCustomer'
import LineItem from '~/ui/components/LineItem/LineItem'
import { formatBytes, formatMinutes } from '~/utils'

const useStyles = makeStyles((theme: CustomTheme) => ({
  lineItem: {
    marginBottom: theme.spacing(2),
  },
  linearProgressText: {
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
  },
}))

type ConsumptionCardProps = {
  subscriptionNonFixed?: Subscription
  subscriptionFixed?: Subscription
}
const ConsumptionCard: React.FC<ConsumptionCardProps> = ({
  subscriptionNonFixed,
  subscriptionFixed,
}) => {
  const { customer } = useCustomer()
  const classes = useStyles()
  const { brand } = useBrand()
  const subscriptionNonFixedId = subscriptionNonFixed?.id || ''
  const subscriptionFixedId = subscriptionFixed?.id || ''
  const tariffId = getTariffId(subscriptionNonFixed)
  const provinceId = String(getProvinceId(customer))
  const { agent } = useSelector(selectAgent)
  const hasConsumptionNonFixedData =
    subscriptionNonFixed && hasConsumptionData(subscriptionNonFixed)

  const {
    getConsumption,
    consumption,
    loading: ConsumptionLoading,
    error: ConsumptionError,
  } = useConsumption(subscriptionNonFixedId)

  const fetchConsumption = () =>
    getConsumption({
      subscriptionId: subscriptionFixedId || subscriptionNonFixedId,
      provinceId,
      brand,
      sub: agent?.sub,
    })

  const { getBonus, bonus, loading: bonusLoading, error: bonusError } = useBonus(
    subscriptionNonFixedId,
  )

  const fetchBonus = () =>
    getBonus({
      subscriptionId: subscriptionNonFixedId,
      tariffId: String(tariffId),
      provinceId,
      brand,
      sub: agent?.sub,
    })

  useEffect(() => {
    if (subscriptionFixedId || subscriptionNonFixedId) {
      fetchConsumption()
    }
  }, [subscriptionFixedId, subscriptionNonFixedId])

  const skeletonBlock = (width: number) => (
    <Box py={1}>
      <Skeleton animation="wave" variant="text" width={`${width}%`} height={24} />
    </Box>
  )

  return (
    <B2CCard title="Contadores de consumo" dataHookPrefix="consumption">
      {customer && hasConsumptionNonFixedData && !ConsumptionError && (
        <>
          <Typography variant="subtitle1">Contador Internet</Typography>
          <Box m={2} />
          {ConsumptionLoading || bonusLoading ? (
            <>
              {skeletonBlock(60)} {skeletonBlock(50)} {skeletonBlock(20)}
            </>
          ) : (
            <>
              <Box display="flex" alignItems="center" mb={3}>
                <Typography variant="caption" className={classes.linearProgressText}>
                  {formatBytes(0, 1, 'GB')}
                </Typography>
                <Box flexGrow="1" mx={4}>
                  <LinearProgress
                    variant="determinate"
                    value={ratioDataConsumed(bonus, consumption) * 100}
                  />
                </Box>
                <Typography variant="caption" className={classes.linearProgressText}>
                  {isUnlimitedDataTariff(bonus)
                    ? '♾️ MB'
                    : formatBytes(totalDataInTariff(bonus), 1, 'GB')}
                </Typography>
              </Box>
              <LineItem
                dataHook="data-rest-consumption-card"
                className={classes.lineItem}
                primaryText="Le quedan"
                secondaryText={
                  isUnlimitedDataTariff(bonus)
                    ? '♾️ MB'
                    : formatBytes(remainingSubscriptionData(bonus, consumption) || 0, 1)
                }
              />

              <LineItem
                dataHook="data-total-consumption-card"
                className={classes.lineItem}
                primaryText="Ha consumido"
                secondaryText={consumption ? formatBytes(dataConsumed(consumption), 1) : '-'}
              />
            </>
          )}
          <Box m={1.5} />
          <DottedDivider />
          <Box m={2} />
        </>
      )}

      {customer && (
        <>
          {ConsumptionError && <ErrorAndRetry action={fetchConsumption} />}
          {bonusError && <ErrorAndRetry action={fetchBonus} />}

          <Typography variant="subtitle1">Contador Llamadas</Typography>
          <Box m={2} />
          {ConsumptionLoading || bonusLoading ? (
            <>
              {skeletonBlock(30)} {skeletonBlock(70)} {skeletonBlock(50)}
            </>
          ) : (
            <>
              <Box display="flex" alignItems="center" mb={3}>
                <Typography variant="caption" className={classes.linearProgressText}>
                  0 Min
                </Typography>
                <Box flexGrow="1" mx={4}>
                  <LinearProgress
                    variant="determinate"
                    value={ratioVoiceConsumed(bonus, consumption) * 100}
                  />
                </Box>
                <Typography variant="caption" className={classes.linearProgressText}>
                  {isUnlimitedVoiceTariff(bonus)
                    ? '♾️ Min'
                    : formatMinutes(totalVoiceInTariff(bonus))}
                </Typography>
              </Box>

              <LineItem
                dataHook="voice-rest-consumption-card"
                className={classes.lineItem}
                primaryText="Le quedan"
                secondaryText={
                  // eslint-disable-next-line no-nested-ternary
                  isUnlimitedVoiceTariff(bonus)
                    ? 'ILIMITADAS'
                    : totalVoiceInTariff(bonus)
                    ? formatMinutes(totalVoiceConsumed(bonus, consumption))
                    : '-'
                }
              />
              <LineItem
                dataHook="voice-total-consumption-card"
                className={classes.lineItem}
                primaryText="Ha consumido"
                secondaryText={
                  (voiceConsumed(consumption) && formatMinutes(voiceConsumed(consumption))) || '-'
                }
              />
            </>
          )}
          {/* 
          <Box m={2} />
          <DottedDivider />
          <Box m={2} />
          <Typography variant="subtitle1">Consumo fuera de tarifa</Typography>
          <Box m={2} />
          {(ConsumptionLoading || bonusLoading) && (
            <>
              {skeletonBlock(30)} {skeletonBlock(70)} {skeletonBlock(50)}
            </>
          )} */}
        </>
      )}
    </B2CCard>
  )
}

export default ConsumptionCard
